import { Parser } from "@siteimprove/alfa-parser";
import { Function as CSSFunction, } from "../../syntax/index.js";
import { Unit } from "../../unit/index.js";
import { List } from "../collection/index.js";
import { Length, LengthPercentage, Numeric } from "../numeric/index.js";
import { Value } from "../value.js";
import { Function } from "./function.js";
const { map, either, parseIf } = Parser;
/**
 * @public
 */
export class Translate extends Function {
    static of(x, y, z) {
        return new Translate(x, y, z);
    }
    _x;
    _y;
    _z;
    constructor(x, y, z) {
        super("translate", Value.hasCalculation(x, y, z));
        this._x = x;
        this._y = y;
        this._z = z;
    }
    get x() {
        return this._x;
    }
    get y() {
        return this._y;
    }
    get z() {
        return this._z;
    }
    resolve(resolver) {
        return new Translate(LengthPercentage.resolve(resolver)(this._x), LengthPercentage.resolve(resolver)(this._y), this._z.resolve(resolver));
    }
    partiallyResolve(resolver) {
        return new Translate(LengthPercentage.partiallyResolve(resolver)(this._x), LengthPercentage.partiallyResolve(resolver)(this._x), this._z.resolve(resolver));
    }
    equals(value) {
        return (value instanceof Translate &&
            value._x.equals(this._x) &&
            value._y.equals(this._y) &&
            value._z.equals(this._z));
    }
    hash(hash) {
        hash.writeHashable(this._x).writeHashable(this._y).writeHashable(this._z);
    }
    toJSON() {
        return {
            ...super.toJSON(),
            x: this._x.toJSON(),
            y: this._y.toJSON(),
            z: this._z.toJSON(),
        };
    }
    toString() {
        if (!this._z.hasCalculation() && Numeric.isZero(this._z)) {
            return `translate(${this._x}${!this._y.hasCalculation() && Numeric.isZero(this._y)
                ? ""
                : `, ${this._y}`})`;
        }
        return `translate3d(${this._x}, ${this._y}, ${this._z})`;
    }
}
/**
 * @public
 */
(function (Translate) {
    function isTranslate(value) {
        return value instanceof Translate;
    }
    Translate.isTranslate = isTranslate;
    const _0 = Length.of(0, Unit.Length.Canonical);
    /**
     * {@link https://drafts.csswg.org/css-transforms/#funcdef-transform-translate}
     */
    const parseTranslate = map(CSSFunction.parse("translate", map(List.parseCommaSeparated(LengthPercentage.parse, 1, 2), (list) => list.values)), ([_, [x, y]]) => Translate.of(x, y ?? _0, _0));
    /**
     * {@link https://drafts.csswg.org/css-transforms/#funcdef-transform-translatex}
     */
    const parseTranslateX = map(CSSFunction.parse("translateX", LengthPercentage.parse), ([_, x]) => Translate.of(x, _0, _0));
    /**
     * {@link https://drafts.csswg.org/css-transforms/#funcdef-transform-translatey}
     */
    const parseTranslateY = map(CSSFunction.parse("translateY", LengthPercentage.parse), ([_, y]) => Translate.of(_0, y, _0));
    /**
     * {@link https://drafts.csswg.org/css-transforms-2/#funcdef-translatez}
     */
    const parseTranslateZ = map(CSSFunction.parse("translateZ", Length.parse), ([_, z]) => Translate.of(_0, _0, z));
    /**
     * {@link https://drafts.csswg.org/css-transforms-2/#funcdef-translate3d}
     */
    const parseTranslate3d = map(CSSFunction.parse("translate3d", parseIf((values) => Length.isLength(values[2]), map(List.parseCommaSeparated(LengthPercentage.parse, 3, 3), (list) => list.values), () => "The z component of translate3d must be a length")), 
    // The type of z is ensured by parseIf.
    ([_, [x, y, z]]) => Translate.of(x, y, z));
    Translate.parse = either(parseTranslate, parseTranslateX, parseTranslateY, parseTranslateZ, parseTranslate3d);
    const parseTranslateProp = map(List.parseSpaceSeparated(LengthPercentage.parse, 1, 2), (list) => {
        const [x, y] = list.values;
        return Translate.of(x, y ?? _0, _0);
    });
    const parseTranslate3dProp = map(parseIf((values) => Length.isLength(values[2]), map(List.parseSpaceSeparated(LengthPercentage.parse, 3, 3), (list) => list.values), () => "The z component of translate3d must be a length"), 
    // The type of z is ensured by parseIf.
    ([x, y, z]) => Translate.of(x, y, z));
    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/CSS/translate}
     */
    Translate.parseProp = either(parseTranslate3dProp, parseTranslateProp);
})(Translate || (Translate = {}));
//# sourceMappingURL=translate.js.map